import { AuthorWeb } from '@nucleus/types/web/posts/author';
import { useWebHostingApi } from '@nucleus/web-theme';
import { QueryFunctionContext, useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';

export const useAuthor = (engineId: string, slugId: string) => {
  const api = useWebHostingApi();

  return useSuspenseQuery(
    api.postHub.collection.read.queryOptions({
      hubId: engineId,
      collectionType: 'authors',
      collectionId: slugId ?? '',
    })
  );
};

export const useFirstValidAuthor = (engineId: string, authorIds: Array<string>) => {
  const api = useWebHostingApi();

  return useSuspenseQueries({
    queries: buildAuthorQueries(api, engineId, authorIds),
    combine: (results) => {
      for (const result of results) {
        if (result.data?.collection !== undefined) {
          return result.data.collection as AuthorWeb | undefined;
        }
      }
      return undefined;
    },
  });
};

export const useAllValidAuthors = (engineId: string, authorIds: Array<string>): Array<AuthorWeb> => {
  const api = useWebHostingApi();

  return useSuspenseQueries({
    queries: buildAuthorQueries(api, engineId, authorIds),
    combine: (results) =>
      results
        .map((result) => result.data?.collection as AuthorWeb | undefined)
        .filter((author): author is AuthorWeb => author?.displayName !== undefined),
  });
};

type WebHostingApi = ReturnType<typeof useWebHostingApi>;

const buildAuthorQueries = (api: WebHostingApi, engineId: string, authorIds: Array<string>) => {
  return authorIds.map((authorId) => {
    const options = api.postHub.collection.read.queryOptions({
      hubId: engineId,
      collectionType: 'authors',
      collectionId: authorId,
      allowArchived: true,
    });

    return {
      queryKey: options.queryKey,
      queryFn: async (context: QueryFunctionContext<typeof options.queryKey>) => {
        try {
          return await options.queryFn!(context);
        } catch {
          return { collection: undefined };
        }
      },
    };
  });
};
